import { ExternalLink, FileText } from 'lucide-react';
import type { TagRequestAsset } from '@/client/types/tag-requests';

const isImage = (a: TagRequestAsset) =>
  (a.mime_type ?? '').startsWith('image/') || /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(a.name ?? '');

interface TagRequestEvidenceProps {
  asset: TagRequestAsset;
  /** The tag this file was attached in support of, when the lister linked one. */
  tagLabel?: string | null;
}

/** One file with the lister's own description — the caption is the reviewer's main context. */
const TagRequestEvidence = ({ asset, tagLabel }: TagRequestEvidenceProps) => {
  const name = asset.name || `File #${asset.asid}`;
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="flex h-40 items-center justify-center bg-gray-50">
        {asset.url && isImage(asset) ? (
          <img src={asset.url} alt={asset.caption || name} className="h-full w-full object-contain" />
        ) : (
          <FileText className="h-10 w-10 text-gray-400" />
        )}
      </div>
      <div className="space-y-1 border-t border-gray-100 px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-xs text-gray-700" title={name}>{name}</span>
          {asset.url ? (
            <a
              href={asset.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-blue-600 hover:underline"
            >
              Open <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            <span className="shrink-0 text-xs text-gray-400">No file URL</span>
          )}
        </div>
        <p className={`text-sm ${asset.caption ? 'text-gray-800' : 'italic text-gray-400'}`}>
          {asset.caption || 'No description given'}
        </p>
        {tagLabel && <p className="text-xs text-gray-500">Supports {tagLabel}</p>}
      </div>
    </div>
  );
};

export default TagRequestEvidence;
